import * as React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useData } from '../hooks/useData'
import { LocationDTO } from '@sales-center/common'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { EntityDetail } from '@user/store/map/typings'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import HomeRepairService from '@mui/icons-material/HomeRepairService'
import PersonIcon from '@mui/icons-material/Person'
import TrackChanges from '@mui/icons-material/TrackChanges'
import GroupsIcon from '@mui/icons-material/People'

type Props = {
  entityDetails: EntityDetail | null
  serviceLocation: LocationDTO
  variant: string
  nameClick?: (entityId: string, variant: string) => void
  viewClick?: (entityId: string, variant: string) => void
  defaultOpen?: boolean
  leftColumnWidth?: number
}

function ExtraDetails(props: Props) {
  const { entityDetails, serviceLocation, variant } = props

  const isPendingLead = variant === 'pendinglead' || variant === 'draftlead'
  const isD2dEnabled = useD2DKnockEnabled()

  const { createdDateFormatted, formattedAltPhone, totalValueFormatted } =
    useData({
      entityDetails,
      serviceLocation,
      variant,
      defaultOpen: props.defaultOpen,
    })

  const detailsLabel = React.useMemo(() => {
    if (isPendingLead) return 'Pending Lead Details'
    if (variant === 'opportunity') return 'Opportunity Details'
    return 'Lead Details'
  }, [isPendingLead, variant])

  const iconSx = { fontSize: 16, color: 'action.active', mt: '4px' }

  if (isD2dEnabled) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            mb: '2px',
          }}
        >
          {detailsLabel}
        </Typography>

        {!isPendingLead && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <HomeRepairService sx={iconSx} />
            <Box>
              <Typography variant="caption" sx={{ color: '#0e283fc4' }}>
                Service
              </Typography>
              {entityDetails?.services?.length ? (
                entityDetails.services.map((s, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ display: 'block' }}
                  >
                    {s.name}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <PersonIcon sx={iconSx} />
          <Box>
            <Typography variant="caption" sx={{ color: '#0e283fc4' }}>
              Owner
            </Typography>
            <Typography variant="body2">
              {entityDetails?.assignee?.fullName || 'Unassigned'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <PersonIcon sx={iconSx} />
          <Box>
            <Typography variant="caption" sx={{ color: '#0e283fc4' }}>
              Created by
            </Typography>
            <Typography variant="body2">
              {entityDetails?.createdBy?.fullName || '-'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TrackChanges sx={iconSx} />
          <Box>
            <Typography variant="caption" sx={{ color: '#0e283fc4' }}>
              Sales Funnel
            </Typography>
            <Typography variant="body2">
              {entityDetails?.salesFunnel || '-'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <GroupsIcon sx={iconSx} />
          <Box>
            <Typography variant="caption" sx={{ color: '#0e283fc4' }}>
              Sales Team
            </Typography>
            <Typography variant="body2">
              {entityDetails?.salesTeam || '-'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    )
  }

  return (
    <Stack gap={0.4} mt={1.2}>
      {!isPendingLead && (
        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
          <Typography variant="subtitle2">Age: </Typography>
          <Typography variant="body2">
            {entityDetails &&
            (entityDetails?.age !== undefined || entityDetails?.age !== null)
              ? `${entityDetails.age} ${entityDetails.age === 1 ? 'day' : 'days'}`
              : '-'}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2">Alt. Phone: </Typography>
        <Typography variant="body2">
          {serviceLocation.altPhoneNumber ? (
            <Link
              target="_blank"
              href={`tel:${formattedAltPhone}`}
              sx={{ textDecoration: 'none', color: '#1565C0' }}
            >
              {formattedAltPhone}
            </Link>
          ) : (
            '-'
          )}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2">Date Created: </Typography>
        <Typography variant="body2">{createdDateFormatted}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2">Created By: </Typography>
        <Typography variant="body2">
          {entityDetails?.createdBy?.fullName || '-'}
        </Typography>
      </Box>
      {!isPendingLead && (
        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
          <Typography variant="subtitle2">Services: </Typography>
          <Typography variant="body2">
            {entityDetails?.services?.length
              ? entityDetails.services.map((s) => s.name).join(', ')
              : '-'}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2">Owner: </Typography>
        <Typography variant="body2">
          {entityDetails?.assignee?.fullName || 'Unassigned'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2" style={{ whiteSpace: 'nowrap' }}>
          Sales Funnel:{' '}
        </Typography>
        <Typography variant="body2">
          {entityDetails?.salesFunnel || '-'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2">Sales Team: </Typography>
        <Typography variant="body2">
          {entityDetails?.salesTeam || '-'}
        </Typography>
      </Box>
      {!isPendingLead && (
        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
          <Typography variant="subtitle2">Total Value: </Typography>
          <Typography variant="body2">{totalValueFormatted}</Typography>
        </Box>
      )}
    </Stack>
  )
}

export default ExtraDetails
